import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, SearchableSelect, useToast, usePincodeLookup,
} from '@kss/ui';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import { customerService, type Customer, type CustomerCreate, type CustomerType, type LoginChannel } from '@kss/api';
import { decodeId } from '../../utils/idCodec';
import {
  validateAadhaar, validateEmail, validateGst, validateMobile, validatePan,
} from '../../utils/formatValidators';

/** Customer-status options for the searchable Status dropdown. */
const STATUS_OPTIONS = [
  { value: 'active',    label: 'Active',    description: 'Can transact normally' },
  { value: 'inactive',  label: 'Inactive',  description: 'Hidden from POS / dine flows' },
  { value: 'pending',   label: 'Pending',   description: 'Awaiting KYC / approval' },
  { value: 'suspended', label: 'Suspended', description: 'Frozen — outstanding dues' },
  { value: 'blocked',   label: 'Blocked',   description: 'Permanently barred' },
];

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[80px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

interface FormState {
  /* basics */
  code: string;
  name: string;
  customerType: CustomerType | '';
  email: string;
  phone: string;
  /* tax & KYC */
  gstin: string;
  pan: string;
  aadhaar: string;
  /* address */
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  /* commerce */
  creditLimit: string;
  paymentTerms: string;
  tags: string;
  customerGroupId: string;
  /* engagement */
  whatsappOptIn: boolean;
  emailOptIn: boolean;
  smsOptIn: boolean;
  doNotDisturb: boolean;
  lastLoginChannel: LoginChannel | '';
  isActive: boolean;
  /** Detailed status (the dropdown). `isActive` is auto-derived from this. */
  statusCode: string;
}

const empty: FormState = {
  code: '', name: '', customerType: 'B2C', email: '', phone: '',
  gstin: '', pan: '', aadhaar: '',
  address: '', city: '', state: '', postalCode: '', country: 'IN',
  creditLimit: '', paymentTerms: '', tags: '', customerGroupId: '',
  whatsappOptIn: true, emailOptIn: true, smsOptIn: false, doNotDisturb: false,
  lastLoginChannel: '', isActive: true, statusCode: 'active',
};

const STEPS = [
  { key: 'basics',     label: 'Basics' },
  { key: 'tax',        label: 'Tax & KYC' },
  { key: 'address',    label: 'Address' },
  { key: 'commerce',   label: 'Commerce' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'review',     label: 'Review' },
];

export default function NewCustomer() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [form, setForm] = useState<FormState>(empty);
  const [step, setStep] = useState(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);
  const [visitedIndices, setVisitedIndices] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Auto-fill city / state / country from the entered PIN code.
  // The hook debounces by 400 ms, caches results, and is a no-op for
  // empty / invalid input — see `packages/ui/src/hooks/usePincodeLookup.ts`.
  const pinLookup = usePincodeLookup(form.postalCode);
  useEffect(() => {
    if (pinLookup.status !== 'ok') return;
    setForm((f) => ({
      ...f,
      // Only overwrite when the field is empty — preserve manual edits.
      city:    f.city    || pinLookup.city    || f.city,
      state:   f.state   || pinLookup.state   || f.state,
      country: f.country && f.country !== 'IN' ? f.country : (pinLookup.country ?? f.country),
    }));
  }, [pinLookup.status, pinLookup.city, pinLookup.state, pinLookup.country]);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: '' }));
  }

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try {
          const r = await customerService.get(id);
          if (cancelled) return;
          setForm({
            ...empty,
            code: r.code ?? '',
            name: r.name ?? '',
            customerType: (r.customerType ?? 'B2C') as CustomerType,
            email: r.email ?? '',
            phone: r.phone ?? '',
            gstin: r.gstin ?? '',
            pan: r.pan ?? '',
            aadhaar: r.aadhaar ?? '',
            address: r.address ?? '',
            city: r.city ?? '',
            state: r.state ?? '',
            postalCode: r.postalCode ?? '',
            country: r.country ?? 'IN',
            creditLimit: r.creditLimit != null ? String(r.creditLimit) : '',
            paymentTerms: r.paymentTerms ?? '',
            tags: r.tags ?? '',
            customerGroupId: r.customerGroupId ?? '',
            whatsappOptIn: r.whatsappOptIn ?? true,
            emailOptIn: r.emailOptIn ?? true,
            smsOptIn: r.smsOptIn ?? false,
            doNotDisturb: r.doNotDisturb ?? false,
            lastLoginChannel: (r.lastLoginChannel ?? '') as LoginChannel | '',
            isActive: !!r.isActive,
            statusCode: r.isActive === false ? 'inactive' : (r as { status?: string }).status ?? 'active',
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  function validate(): string | null {
    if (!form.name.trim()) return 'Name is required';
    if (form.email) {
      const e = validateEmail(form.email);
      if (e) return e;
    }
    if (form.phone) {
      const e = validateMobile(form.phone);
      if (e) return e;
    }
    if (form.gstin) {
      const e = validateGst(form.gstin);
      if (e) return e;
    }
    if (form.pan) {
      const e = validatePan(form.pan);
      if (e) return e;
    }
    if (form.aadhaar) {
      const e = validateAadhaar(form.aadhaar);
      if (e) return e;
    }
    return null;
  }

  async function save() {
    const e = validate();
    if (e) { toast.error('Fix errors', e); return; }
    setSaving(true);
    try {
      const payload: CustomerCreate = {
        code: form.code || undefined,
        name: form.name,
        customerType: (form.customerType || undefined) as CustomerType | undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        gstin: form.gstin || undefined,
        pan: form.pan || undefined,
        aadhaar: form.aadhaar || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        creditLimit: form.creditLimit ? Number(form.creditLimit) : undefined,
        paymentTerms: form.paymentTerms || undefined,
        tags: form.tags || undefined,
        customerGroupId: form.customerGroupId || undefined,
        whatsappOptIn: form.whatsappOptIn,
        emailOptIn: form.emailOptIn,
        smsOptIn: form.smsOptIn,
        doNotDisturb: form.doNotDisturb,
        lastLoginChannel: (form.lastLoginChannel || undefined) as LoginChannel | undefined,
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await customerService.update([{ customerId: id, ...payload } as Customer]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        await customerService.create([payload]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/customers');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally { setSaving(false); }
  }

  const isLast = step === STEPS.length - 1;
  function goNext() {
    if (step === 0 && !form.name.trim()) {
      toast.error('Fix errors', 'Name is required'); return;
    }
    setSavedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(step + 1);
  }
  function goBack() {
    setVisitedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(Math.max(0, step - 1));
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/customers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit customer' : 'New customer'}
          </h2>
        </div>
        <div className="mb-3">
          <ArrowTabStepper
            compact
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            activeIndex={step}
            savedIndices={savedIndices}
            visitedIndices={visitedIndices}
            onChange={(i) => setStep(i)}
          />
        </div>

        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <>
            {step === 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Code</span>
                  <input className={inputCls} value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="CUST-0001 (auto if empty)" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
                  <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Retail Pvt Ltd / Ramesh Kumar" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Customer type</span>
                  <select className={selectCls} value={form.customerType} onChange={(e) => set('customerType', e.target.value as FormState['customerType'])}>
                    <option value="B2C">B2C — End customer</option>
                    <option value="B2B">B2B — Business buyer</option>
                    <option value="B2B2C">B2B2C — Reseller</option>
                  </select>
                </label>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Status</span>
                  <SearchableSelect
                    value={form.statusCode}
                    onChange={(v) => {
                      set('statusCode', v);
                      // `isActive` derived: only "active" is live; everything
                      // else is treated as inactive in the legacy boolean.
                      set('isActive', v === 'active');
                    }}
                    options={STATUS_OPTIONS}
                    placeholder="Select status…"
                    searchPlaceholder="Filter statuses…"
                  />
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Mobile <span className="text-danger">*</span></span>
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => {
                      if (form.phone) {
                        const err = validateMobile(form.phone);
                        if (err) setErrors((s) => ({ ...s, phone: err }));
                      }
                    }}
                    maxLength={10}
                    placeholder="98XXXXXXXX"
                  />
                  {errors.phone && <span className="text-[11px] text-danger">{errors.phone}</span>}
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Email</span>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    onBlur={() => {
                      if (form.email) {
                        const err = validateEmail(form.email);
                        if (err) setErrors((s) => ({ ...s, email: err }));
                      }
                    }}
                    placeholder="name@company.com"
                  />
                  {errors.email && <span className="text-[11px] text-danger">{errors.email}</span>}
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">GSTIN</span>
                  <input
                    className={inputCls}
                    value={form.gstin}
                    onChange={(e) => set('gstin', e.target.value.toUpperCase())}
                    onBlur={() => {
                      if (form.gstin) {
                        const err = validateGst(form.gstin);
                        if (err) setErrors((s) => ({ ...s, gstin: err }));
                      }
                    }}
                    maxLength={15}
                    placeholder="22AAAAA0000A1Z5"
                  />
                  {errors.gstin && <span className="text-[11px] text-danger">{errors.gstin}</span>}
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">PAN</span>
                  <input
                    className={inputCls}
                    value={form.pan}
                    onChange={(e) => set('pan', e.target.value.toUpperCase())}
                    onBlur={() => {
                      if (form.pan) {
                        const err = validatePan(form.pan);
                        if (err) setErrors((s) => ({ ...s, pan: err }));
                      }
                    }}
                    maxLength={10}
                    placeholder="ABCDE1234F"
                  />
                  {errors.pan && <span className="text-[11px] text-danger">{errors.pan}</span>}
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Aadhaar (B2C)</span>
                  <input
                    className={inputCls}
                    value={form.aadhaar}
                    onChange={(e) => set('aadhaar', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => {
                      if (form.aadhaar) {
                        const err = validateAadhaar(form.aadhaar);
                        if (err) setErrors((s) => ({ ...s, aadhaar: err }));
                      }
                    }}
                    maxLength={12}
                    placeholder="1234 5678 9012"
                  />
                  {errors.aadhaar && <span className="text-[11px] text-danger">{errors.aadhaar}</span>}
                </label>
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Address</span>
                  <textarea className={textareaCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
                </label>
                {/* PIN first so the auto-fill flows naturally into the other fields below. */}
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">
                    Postal code{' '}
                    {pinLookup.status === 'loading' && <span className="text-content-muted">(looking up…)</span>}
                    {pinLookup.status === 'ok'      && <span className="text-success">(auto-fill ready)</span>}
                    {pinLookup.status === 'error'   && <span className="text-warning">(not found)</span>}
                    {pinLookup.status === 'invalid' && <span className="text-warning">(6 digits)</span>}
                  </span>
                  <input
                    className={inputCls}
                    value={form.postalCode}
                    onChange={(e) => set('postalCode', e.target.value.replace(/\D/g, ''))}
                    maxLength={6}
                    placeholder="e.g. 302020"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">City</span>
                  <input
                    className={inputCls}
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    placeholder={pinLookup.city ?? 'e.g. Jaipur'}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">State</span>
                  <input
                    className={inputCls}
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    placeholder={pinLookup.state ?? 'e.g. Rajasthan'}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Country</span>
                  <input
                    className={inputCls}
                    value={form.country}
                    onChange={(e) => set('country', e.target.value)}
                    placeholder={pinLookup.country ?? 'India'}
                  />
                </label>
                <p className="text-[11px] text-content-muted md:col-span-2">
                  Tip: enter a 6-digit Indian PIN and we&rsquo;ll fill in city, state &amp; country.
                </p>
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Credit limit (₹)</span>
                  <input type="number" className={inputCls} value={form.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Payment terms</span>
                  <input className={inputCls} value={form.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} placeholder="Net 30, COD, …" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Customer group ID</span>
                  <input className={inputCls} value={form.customerGroupId} onChange={(e) => set('customerGroupId', e.target.value)} placeholder="(reserved — picker coming)" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Tags</span>
                  <input className={inputCls} value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="vip, wholesale, premium" />
                </label>
              </div>
            )}

            {step === 4 && (
              <div className="grid gap-3 md:grid-cols-2">
                <ToggleRow label="WhatsApp opt-in"  hint="Used for magic-link auth + order updates" value={form.whatsappOptIn}  onChange={(v) => set('whatsappOptIn', v)} />
                <ToggleRow label="Email opt-in"     hint="Promotional + transactional"            value={form.emailOptIn}     onChange={(v) => set('emailOptIn', v)} />
                <ToggleRow label="SMS opt-in"       hint="Plain SMS notifications"                value={form.smsOptIn}       onChange={(v) => set('smsOptIn', v)} />
                <ToggleRow label="Do not disturb"   hint="Mutes ALL marketing channels"           value={form.doNotDisturb}   onChange={(v) => set('doNotDisturb', v)} />
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Last login channel</span>
                  <select className={selectCls} value={form.lastLoginChannel} onChange={(e) => set('lastLoginChannel', e.target.value as FormState['lastLoginChannel'])}>
                    <option value="">—</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Email">Email</option>
                    <option value="Mobile">Mobile</option>
                    <option value="Web">Web</option>
                    <option value="App">App</option>
                  </select>
                </label>
              </div>
            )}

            {step === 5 && (
              <div className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-2">
                <Row k="Code" v={form.code || '—'} />
                <Row k="Name" v={form.name} />
                <Row k="Type" v={form.customerType || '—'} />
                <Row k="Mobile" v={form.phone || '—'} />
                <Row k="Email" v={form.email || '—'} />
                <Row k="GSTIN" v={form.gstin || '—'} />
                <Row k="PAN" v={form.pan || '—'} />
                <Row k="Address" v={[form.address, form.city, form.state, form.postalCode].filter(Boolean).join(', ') || '—'} />
                <Row k="Credit limit" v={form.creditLimit ? `₹${form.creditLimit}` : '—'} />
                <Row k="Payment terms" v={form.paymentTerms || '—'} />
                <Row k="Tags" v={form.tags || '—'} />
                <Row k="Channels" v={[
                  form.whatsappOptIn && 'WhatsApp',
                  form.emailOptIn && 'Email',
                  form.smsOptIn && 'SMS',
                  form.doNotDisturb && '🚫 DND',
                ].filter(Boolean).join(', ') || '—'} />
                <Row k="Status" v={form.isActive ? 'Active' : 'Inactive'} />
              </div>
            )}
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="text-xs text-content-muted">Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/panel/customers')} disabled={saving}>Cancel</Button>
            {step > 0 && <Button variant="outline" onClick={goBack} disabled={saving}><ArrowLeft className="h-4 w-4" /> Back</Button>}
            {!isLast && <Button onClick={goNext} disabled={saving}>Next <ArrowRight className="h-4 w-4" /></Button>}
            {isLast && (
              <Button onClick={save} disabled={saving || loading}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save customer'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-content-muted">{k}</span>
      <span className="text-sm font-medium text-content-primary">{v || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}

function ToggleRow({
  label, hint, value, onChange,
}: { label: string; hint: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-surface-card-hover">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-primary" />
      <div className="flex-1">
        <div className="text-sm font-medium text-content-primary">{label}</div>
        <div className="text-xs text-content-muted">{hint}</div>
      </div>
    </label>
  );
}
